import Papa from 'papaparse';

// ---------------------------------------------------------------------------
// CSV IEP-goals import — small pure-function library used by the goals
// import wizard (src/pages/goals-import).
//
// Design goals mirror csvRoster.js:
//
//   * Feed an Excel export straight back in. Every IEP-management platform
//     (EdPlan / SEIS / Frontline / IEP Direct / Enrich / SchoolMint /
//     Goalbook / SpEd Forms, etc.) exports something CSV-ish. HEADER_SYNONYMS
//     below auto-maps a bunch of common variants — "Goal Text", "Description",
//     "Measurable", "Baseline / Present Levels", "Objective" — onto our
//     canonical field keys so the teacher hits "Next" instead of a mapping
//     puzzle.
//
//   * Match rows to the existing student roster. If the CSV has a "Student"
//     column with a full name (or first + last columns), the wizard needs to
//     resolve it to a real students.id before the row can be inserted into
//     behavior_goals. matchStudentByName() below is the workhorse; it does
//     a case-insensitive, punctuation-stripped comparison and returns a
//     single match, "no match", or "ambiguous — 2+ students match the name".
//
//   * Never lose data on malformed rows. validateDraft() tags each row
//     with { valid, warnings, errors } so the UI can say "18 goals will
//     import, 2 rows can't find their student, fix them or skip them."
//     Skipping a bad row never blocks the good ones.
//
//   * Detect probable duplicates against the existing goals list. Match on
//     studentId + goalArea + first-80-chars-of-specific so re-importing
//     the same file the next day doesn't stamp 20 duplicate goals into
//     the roster.
//
// Pure JS. No React or Supabase deps — easy to unit-test.
// ---------------------------------------------------------------------------

// The canonical field keys we map every CSV column onto. Order matters
// only for the mapping-dropdown UI, which shows them in this order.
export const CANONICAL_FIELDS = [
  {
    key: 'studentName',
    label: 'Student — full name',
    required: false,
    hint: 'Match to your roster by "First Last" or "Last, First".',
  },
  {
    key: 'studentFirstName',
    label: 'Student — first name',
    required: false,
    hint: 'Combine with a Last name column when your CSV has them split.',
  },
  {
    key: 'studentLastName',
    label: 'Student — last name',
    required: false,
    hint: 'Combine with a First name column when your CSV has them split.',
  },
  {
    key: 'goalArea',
    label: 'Goal area / domain',
    required: false,
    hint: 'Academic, Behavioral, Communication, Motor, Functional, etc.',
  },
  {
    key: 'specific',
    label: 'Goal text (what will improve)',
    required: true,
    hint: 'The full goal statement — required.',
  },
  {
    key: 'measurable',
    label: 'How progress is measured',
    required: true,
    hint: 'How you\'ll count / time / observe progress — required.',
  },
  {
    key: 'baseline',
    label: 'Baseline / present levels',
    required: false,
    hint: 'Where the student is starting from.',
  },
  {
    key: 'target',
    label: 'Target level',
    required: true,
    hint: 'Where you want the student to get to — required.',
  },
  {
    key: 'masteryCriteria',
    label: 'Mastery criteria',
    required: false,
    hint: '"3 consecutive weeks at 80%", etc.',
  },
  {
    key: 'targetDate',
    label: 'Target date',
    required: false,
    hint: 'When you\'d like the goal to be met by.',
  },
  {
    key: 'targetValue',
    label: 'Target number (numeric)',
    required: false,
    hint: 'A single number, e.g. "80" for 80%.',
  },
  {
    key: 'unit',
    label: 'Unit',
    required: false,
    hint: 'e.g. %, words per minute, minutes.',
  },
  {
    key: 'status',
    label: 'Status',
    required: false,
    hint: 'Not started / In progress / Mastered / Discontinued.',
  },
  // Special sentinel — mapping a CSV column to "ignore" drops it on
  // the floor.
  {
    key: '__ignore__',
    label: 'Ignore this column',
    required: false,
    hint: 'Skip this column when importing.',
  },
];

// Grouped by canonical key. Match is case-insensitive and ignores
// punctuation and whitespace, so "Goal-Text", "goal text", "GOAL_TEXT"
// and "goaltext" all collapse to the same normalized token. See
// normalizeHeader() below.
const HEADER_SYNONYMS = {
  studentName: [
    'student', 'studentname', 'name', 'fullname', 'studentfullname',
    'child', 'childname', 'learner', 'learnername', 'pupil', 'pupilname',
  ],
  studentFirstName: [
    'firstname', 'first', 'givenname', 'given', 'fname',
    'studentfirstname', 'studentfirst',
  ],
  studentLastName: [
    'lastname', 'last', 'familyname', 'surname', 'lname',
    'studentlastname', 'studentlast',
  ],
  goalArea: [
    'goalarea', 'area', 'domain', 'goaldomain', 'category', 'goalcategory',
    'skillarea', 'strand', 'discipline',
  ],
  specific: [
    'specific', 'goal', 'goaltext', 'goalstatement', 'statement',
    'annualgoal', 'annualgoaltext', 'description', 'goaldescription',
    'text', 'whatwilllchange', 'objective', 'objectivetext',
  ],
  measurable: [
    'measurable', 'measurement', 'measuredby', 'howmeasured',
    'measurementmethod', 'progressmeasurement', 'evaluationmethod',
    'assessmentmethod', 'method',
  ],
  baseline: [
    'baseline', 'currentlevel', 'currentperformance', 'presentlevels',
    'presentlevel', 'plaafp', 'plep', 'startinglevel', 'startingpoint',
    'currentbaseline',
  ],
  target: [
    'target', 'targetlevel', 'goaltarget', 'expectedoutcome', 'outcome',
    'objectivetarget', 'endgoal', 'targetperformance', 'benchmarktarget',
  ],
  masteryCriteria: [
    'mastery', 'masterycriteria', 'criteria', 'criterion',
    'criteriaformastery', 'successcriteria', 'evaluationcriteria',
    'accuracylevel',
  ],
  targetDate: [
    'targetdate', 'duedate', 'goaldate', 'enddate', 'endby',
    'projectedcompletion', 'projectedcompletiondate', 'byiependdate',
    'iependdate', 'reviewdate',
  ],
  targetValue: [
    'targetvalue', 'targetnumber', 'targetnumeric', 'goalnumber',
    'targetscore', 'targetpercent', 'goaltargetvalue',
  ],
  unit: [
    'unit', 'units', 'measurementunit', 'metricunit', 'measure',
  ],
  status: [
    'status', 'goalstatus', 'progressstatus', 'state', 'goalstate',
  ],
};

// Common goal-area labels every IEP platform stamps on their export.
// Anything we don't recognize falls through as free text into the
// primary goalArea, which AddGoalModal also renders (though only the
// dropdown values map to filter chips on the goals page).
const GOAL_AREA_SYNONYMS = {
  academic: [
    'academic', 'academics', 'reading', 'math', 'writing', 'literacy',
    'ela', 'englishlanguagearts', 'science', 'socialstudies',
  ],
  behavioral: [
    'behavior', 'behavioral', 'behaviour', 'behaviour', 'conduct',
    'selfregulation', 'selfregulatory',
  ],
  'social-emotional': [
    'social', 'socialemotional', 'socialemotionallearning', 'sel',
    'emotionalregulation', 'socialskills',
  ],
  communication: [
    'communication', 'speech', 'language', 'speechlanguage', 'slp',
    'articulation', 'expressivelanguage', 'receptivelanguage',
    'pragmaticlanguage', 'aac',
  ],
  functional: [
    'functional', 'lifeskills', 'adaptive', 'adaptiveskills', 'daily',
    'dailyliving', 'selfhelp', 'selfcare', 'independentliving',
  ],
  motor: [
    'motor', 'finemotor', 'grossmotor', 'ot', 'occupationaltherapy',
    'pt', 'physicaltherapy', 'motorskills',
  ],
  transition: [
    'transition', 'transitionservices', 'postsecondary', 'postschool',
    'vocational', 'employment', 'careertransition',
  ],
  other: ['other', 'general', 'misc', 'miscellaneous'],
};

const STATUS_SYNONYMS = {
  not_started: [
    'notstarted', 'notstartedyet', 'new', 'notbegun', 'pending',
    'notyetstarted', 'notyet',
  ],
  in_progress: [
    'inprogress', 'active', 'working', 'ongoing', 'started', 'current',
    'workingon', 'inprocess',
  ],
  mastered: [
    'mastered', 'complete', 'completed', 'met', 'achieved', 'accomplished',
    'goalmet',
  ],
  discontinued: [
    'discontinued', 'discontinue', 'dropped', 'removed', 'ended',
    'terminated', 'inactive', 'abandoned',
  ],
};

// Squash "Goal-Text", "goal text", "GOAL_TEXT" → "goaltext".
function normalizeHeader(raw) {
  if (raw == null) return '';
  return String(raw).toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Try to auto-map a CSV header string onto one of our canonical fields
// by comparing its normalized form against the synonym table. Returns
// null if no match — the UI shows "Ignore" as the default in that
// case, and the teacher can override to any field.
export function suggestFieldForHeader(rawHeader) {
  const norm = normalizeHeader(rawHeader);
  if (!norm) return null;
  for (const [key, aliases] of Object.entries(HEADER_SYNONYMS)) {
    if (aliases.includes(norm)) return key;
  }
  return null;
}

// Parse the raw file text into { headers, rows, errors }. Same defaults
// as csvRoster.parseCsvText — see that file for the rationale on each
// PapaParse flag.
export function parseCsvText(text) {
  if (!text || typeof text !== 'string') {
    return { headers: [], rows: [], errors: [{ message: 'File was empty.' }] };
  }
  const cleaned = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const result = Papa.parse(cleaned, {
    header: false,
    skipEmptyLines: 'greedy',
    dynamicTyping: false,
  });
  const grid = Array.isArray(result?.data) ? result.data : [];
  if (grid.length === 0) {
    return {
      headers: [],
      rows: [],
      errors: [{ message: 'The file didn\'t contain any rows.' }],
    };
  }
  const headers = (grid[0] || []).map((h) => (h == null ? '' : String(h).trim()));
  const rows = grid.slice(1).map((row, idx) => {
    const obj = {};
    headers.forEach((h, colIdx) => {
      obj[h] = row[colIdx] == null ? '' : String(row[colIdx]).trim();
    });
    obj.__rowIndex = idx + 2; // +1 to skip header, +1 to be 1-based
    return obj;
  });
  return { headers, rows, errors: result?.errors || [] };
}

// Build the initial mapping suggestion: { csvHeader: canonicalKey }.
export function buildInitialMapping(headers) {
  const mapping = {};
  const used = new Set();
  for (const header of headers) {
    if (!header) continue;
    const suggested = suggestFieldForHeader(header);
    // studentName + studentFirstName + studentLastName are all valid at
    // the same time (some SIS exports include a "Full Name" column AND
    // separate first/last columns; we prefer the split when both exist,
    // so the "used" set only guards against duplicates of the SAME key).
    if (suggested && !used.has(suggested)) {
      mapping[header] = suggested;
      used.add(suggested);
    } else {
      mapping[header] = '__ignore__';
    }
  }
  return mapping;
}

// Parse a date string into YYYY-MM-DD. Same rules as csvRoster —
// factored inline so this module has no dep on that one.
export function coerceDate(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const isoMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const usMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/);
  if (usMatch) {
    let [, m, d, y] = usMatch;
    if (y.length === 2) {
      const yr = parseInt(y, 10);
      y = yr <= 30 ? String(2000 + yr) : String(1900 + yr);
    }
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const d = new Date(trimmed);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return null;
}

// Normalize a goalArea onto AddGoalModal's dropdown values. Unknown
// values are kept verbatim so nothing gets destroyed.
export function coerceGoalArea(raw) {
  if (raw == null) return '';
  const norm = String(raw).toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  if (!norm) return '';
  for (const [canonical, aliases] of Object.entries(GOAL_AREA_SYNONYMS)) {
    if (aliases.includes(norm)) return canonical;
  }
  return String(raw).trim();
}

// Normalize a status column onto the four values behavior_goals.status
// accepts. Anything unrecognized returns '' and validateDraft() then
// warns "Status wasn't recognized — will default to In progress."
export function coerceStatus(raw) {
  if (raw == null) return '';
  const norm = String(raw).toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  if (!norm) return '';
  for (const [canonical, aliases] of Object.entries(STATUS_SYNONYMS)) {
    if (aliases.includes(norm)) return canonical;
  }
  return '';
}

// Numeric coercion for targetValue. Strips a trailing "%" or a leading
// "$" the same way spreadsheet cells sometimes carry them, then parses.
// Returns null if nothing usable.
export function coerceNumber(raw) {
  if (raw == null || raw === '') return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/[$%,\s]/g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Squash "O'Malley-Ramirez  " → "omalleyramirez" for a robust name
// comparison. Handles hyphens, apostrophes, punctuation, extra spaces.
function normalizePersonName(raw) {
  if (raw == null) return '';
  return String(raw).toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Given the mapping and the raw row, extract a candidate student name
// (either from studentName, or from first+last combined). Returns an
// object of { firstName, lastName, fullName } for downstream matching.
// If none of the three name columns are mapped, returns null so the UI
// can flag the row.
function extractRowStudentName(row, mapping) {
  let full = '';
  let first = '';
  let last = '';
  for (const [csvHeader, canonicalKey] of Object.entries(mapping)) {
    if (!canonicalKey || canonicalKey === '__ignore__') continue;
    const value = row[csvHeader];
    if (value == null || value === '') continue;
    switch (canonicalKey) {
      case 'studentName':
        full = String(value).trim();
        break;
      case 'studentFirstName':
        first = String(value).trim();
        break;
      case 'studentLastName':
        last = String(value).trim();
        break;
      default:
        break;
    }
  }
  if (!full && !first && !last) return null;

  // If we have both first+last, prefer those; otherwise try to split
  // the fullName. Support both "First Last" and "Last, First" —
  // districts export both.
  if (!first && !last && full) {
    if (full.includes(',')) {
      const [l, ...rest] = full.split(',');
      last = (l || '').trim();
      first = rest.join(',').trim();
    } else {
      const parts = full.split(/\s+/);
      if (parts.length === 1) {
        first = parts[0];
      } else {
        first = parts[0];
        last = parts.slice(1).join(' ');
      }
    }
  }
  return {
    firstName: first,
    lastName: last,
    fullName: full || `${first} ${last}`.trim(),
  };
}

// Match a candidate name against the roster. Returns:
//   { kind: 'match', student }        exactly one student matched
//   { kind: 'multi', candidates }     2+ students matched (ambiguous)
//   { kind: 'none' }                  no match
//
// Matching rules, in order of preference:
//   1. First + last both match (case- and punctuation-insensitive).
//   2. Only one of the two is present in the CSV, but exactly one
//      student on the roster shares it.
//   3. Fall back to comparing the concatenated fullName.
export function matchStudentByName(candidate, roster) {
  if (!candidate) return { kind: 'none' };
  const list = Array.isArray(roster) ? roster : [];
  if (list.length === 0) return { kind: 'none' };

  const first = normalizePersonName(candidate.firstName);
  const last = normalizePersonName(candidate.lastName);
  const full = normalizePersonName(candidate.fullName);

  // Pass 1: first + last both present and both match.
  if (first && last) {
    const exact = list.filter((s) => {
      const f = normalizePersonName(s.firstName);
      const l = normalizePersonName(s.lastName);
      return f === first && l === last;
    });
    if (exact.length === 1) return { kind: 'match', student: exact[0] };
    if (exact.length > 1) return { kind: 'multi', candidates: exact };
  }

  // Pass 2: only last name known.
  if (!first && last) {
    const partial = list.filter(
      (s) => normalizePersonName(s.lastName) === last,
    );
    if (partial.length === 1) return { kind: 'match', student: partial[0] };
    if (partial.length > 1) return { kind: 'multi', candidates: partial };
  }

  // Pass 3: only first name known.
  if (first && !last) {
    const partial = list.filter(
      (s) => normalizePersonName(s.firstName) === first,
    );
    if (partial.length === 1) return { kind: 'match', student: partial[0] };
    if (partial.length > 1) return { kind: 'multi', candidates: partial };
  }

  // Pass 4: fullName comparison.
  if (full) {
    const fuzzy = list.filter((s) => {
      const concat = normalizePersonName(
        `${s.firstName || ''}${s.lastName || ''}`,
      );
      return concat === full;
    });
    if (fuzzy.length === 1) return { kind: 'match', student: fuzzy[0] };
    if (fuzzy.length > 1) return { kind: 'multi', candidates: fuzzy };
  }

  return { kind: 'none' };
}

// Apply the mapping to a raw parsed row → returns { draft, studentMatch }.
// draft is the goal shape our addGoal() function accepts (plus a
// resolved studentId when we found one). studentMatch surfaces the raw
// name the user typed + the outcome (match / multi / none) so the UI
// can show a friendly warning row instead of a silent skip.
export function rowToGoalDraft(row, mapping, roster) {
  const draft = {
    studentId: '',
    goalArea: '',
    specific: '',
    measurable: '',
    baseline: '',
    target: '',
    masteryCriteria: '',
    targetDate: null,
    targetValue: null,
    unit: '',
    status: '',
  };

  for (const [csvHeader, canonicalKey] of Object.entries(mapping)) {
    if (!canonicalKey || canonicalKey === '__ignore__') continue;
    const value = row[csvHeader];
    if (value == null || value === '') continue;
    switch (canonicalKey) {
      case 'goalArea':
        draft.goalArea = coerceGoalArea(value);
        break;
      case 'specific':
        draft.specific = String(value).trim();
        break;
      case 'measurable':
        draft.measurable = String(value).trim();
        break;
      case 'baseline':
        draft.baseline = String(value).trim();
        break;
      case 'target':
        draft.target = String(value).trim();
        break;
      case 'masteryCriteria':
        draft.masteryCriteria = String(value).trim();
        break;
      case 'targetDate':
        draft.targetDate = coerceDate(value);
        break;
      case 'targetValue':
        draft.targetValue = coerceNumber(value);
        break;
      case 'unit':
        draft.unit = String(value).trim();
        break;
      case 'status':
        draft.status = coerceStatus(value);
        break;
      default:
        break;
    }
  }

  // Resolve the student.
  const nameFromRow = extractRowStudentName(row, mapping);
  const match = nameFromRow
    ? matchStudentByName(nameFromRow, roster)
    : { kind: 'none' };
  if (match.kind === 'match') {
    draft.studentId = match.student.id;
  }
  return {
    draft,
    studentMatch: {
      ...match,
      // Echo the raw name back so the UI can render "Couldn't find
      // «Joe Jones» on your roster" instead of just "No student."
      rawName: nameFromRow?.fullName || '',
    },
  };
}

// Validate a draft row. Returns { valid, warnings[], errors[] }. `valid`
// (importable) means no errors — warnings still let the row through
// but flag it for the teacher to review.
export function validateDraft(draft, studentMatch) {
  const errors = [];
  const warnings = [];

  // Blocker: student couldn't be resolved.
  if (!draft.studentId) {
    if (!studentMatch || studentMatch.kind === 'none') {
      const raw = studentMatch?.rawName;
      errors.push(
        raw
          ? `No student on your roster matches "${raw}".`
          : 'No student column mapped, or the name column was blank.',
      );
    } else if (studentMatch.kind === 'multi') {
      const raw = studentMatch.rawName || 'this student';
      const n = studentMatch.candidates?.length || 0;
      errors.push(
        `"${raw}" matches ${n} students on your roster — needs a unique name (or add a Date of Birth column later).`,
      );
    }
  }

  // The behavior_goals table requires goal_area / specific / measurable /
  // target to be NOT NULL. We enforce the same at import time so the
  // Supabase insert doesn't 400 out.
  if (!draft.goalArea) {
    // If it's totally blank, warn — we'll default to "other" so the
    // insert doesn't blow up. A teacher can bulk-fix afterward on the
    // goals page.
    warnings.push('Goal area was blank — will default to "Other".');
  }
  if (!draft.specific) errors.push('Missing goal text.');
  if (!draft.measurable) errors.push('Missing "How progress is measured."');
  if (!draft.target) errors.push('Missing "Target level."');

  // Status coercion: coerceStatus() returns '' for unrecognized values.
  // We'll default to in_progress downstream, but warn so the teacher
  // can bulk-fix.
  if (draft.status && !['not_started', 'in_progress', 'mastered', 'discontinued'].includes(draft.status)) {
    warnings.push(`Status "${draft.status}" wasn't recognized — will default to In progress.`);
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  };
}

// Best-effort duplicate detection against the existing goals list.
// Returns array of `{ existingId, reason }`. Match rule: same
// studentId + same goalArea + same first-80 chars of specific
// (case-insensitive). That catches "re-imported the same file again"
// without false-positiving on a Reading goal that also happens to
// share an area with the existing Math goal.
export function findLikelyDuplicates(draft, existingGoals) {
  if (!Array.isArray(existingGoals) || existingGoals.length === 0) return [];
  if (!draft.studentId || !draft.specific) return [];
  const key = String(draft.specific).slice(0, 80).toLowerCase().trim();
  const matches = [];
  for (const goal of existingGoals) {
    if (goal.studentId !== draft.studentId) continue;
    if ((goal.goalArea || '') !== (draft.goalArea || '')) continue;
    const existingKey = String(goal.specific || '').slice(0, 80).toLowerCase().trim();
    if (existingKey === key) {
      matches.push({
        existingId: goal.id,
        reason: 'Same student + area + goal text already on file',
      });
    }
  }
  return matches;
}

// Build a downloadable CSV template with all our canonical headers plus
// a couple of illustrative example rows. Excel will open this without
// warnings.
export function buildCsvTemplate() {
  const headers = [
    'Student',
    'Goal Area',
    'Goal Text',
    'How Measured',
    'Baseline',
    'Target',
    'Mastery Criteria',
    'Target Date',
    'Target Number',
    'Unit',
    'Status',
  ];
  const examples = [
    [
      'Joe Jones',
      'Academic',
      'Given a grade-level passage and 2 visual supports, Joe will identify the main idea and 2 supporting details.',
      '% of comprehension questions answered correctly across 3 weekly trials',
      '40% accuracy on baseline probes',
      '80% accuracy across 3 consecutive weekly probes',
      '3 consecutive weeks at 80% or higher',
      '9/1/2026',
      '80',
      '%',
      'In progress',
    ],
    [
      'Garcia, Maria',
      'Communication',
      'During 15-minute structured conversations, Maria will initiate a topic and respond to a partner\'s question.',
      'Frequency count of independent topic initiations across 5 sessions',
      '1 initiation per 15 minutes',
      '4 initiations per 15 minutes across 3 consecutive sessions',
      '3 consecutive sessions at target across 2 settings',
      '',
      '4',
      'initiations',
      'Not started',
    ],
  ];
  return Papa.unparse([headers, ...examples]);
}
